import { apiClient } from './api';
import {
  SmsConversation,
  SmsMessage,
  SmsCampaignStats,
  SmsCampaignResultLead,
  SmsGlobalTemplate,
  SmsGlobalTemplateStep,
  SmsGlobalTemplateType,
} from '../types/sms';
import { CallAttempt, LeadNote, LeadTimelineEvent } from '../types/lead';

export interface MerchantFileData {
  lead: {
    id: string;
    fullName?: string;
    companyName?: string;
    status: string;
    subStageId?: string | null;
    subStageName?: string | null;
    followUpDate?: string | null;
    campaignId?: string;
  };
  notes: LeadNote[];
  calls: CallAttempt[];
  documents: {
    categories: Array<{
      id: string;
      categorySlug: string;
      label: string;
      status: string;
      fileCount: number;
    }>;
    documents: Array<{
      id: string;
      categoryId?: string | null;
      bankAccountId?: string | null;
      fileName: string;
      fileSizeBytes?: number | null;
      createdAt: string;
    }>;
    bankAccounts: Array<{
      id: string;
      label: string;
      accountLast4?: string | null;
      status: string;
    }>;
    application: {
      applicationUrl?: string | null;
      linkSentAt?: string | null;
      linkOpenedAt?: string | null;
      appDoneAt?: string | null;
      docsUploadedAt?: string | null;
      completedAt?: string | null;
    } | null;
  };
  timeline: LeadTimelineEvent[];
}

export interface SmsConversationListResult {
  conversations: SmsConversation[];
  hasMore: boolean;
  nextBefore: string | null;
}

export const smsService = {
  async getConversations(params?: {
    status?: string;
    leadStatus?: string;
    followUpDateFrom?: string;
    followUpDateTo?: string;
    campaignId?: string;
    assignedAgentId?: string;
    unreadOnly?: boolean;
    starredOnly?: boolean;
    search?: string;
    limit?: number;
    before?: string;
  }): Promise<SmsConversationListResult> {
    const response = await apiClient.get<any>('/sms/conversations', { params });
    return (
      response.data.data || {
        conversations: [],
        hasMore: false,
        nextBefore: null,
      }
    );
  },

  async getConversationSummary(conversationId: string): Promise<SmsConversation> {
    const response = await apiClient.get<any>(`/sms/conversations/${conversationId}/summary`);
    return response.data.data;
  },

  async getConversationForLead(leadId: string): Promise<SmsConversation> {
    const response = await apiClient.get<any>(`/sms/leads/${leadId}/conversation`);
    return response.data.data;
  },

  async getUnreadConversationCount(params?: {
    campaignId?: string;
    assignedAgentId?: string;
  }): Promise<number> {
    const response = await apiClient.get<any>('/sms/conversations/unread-count', { params });
    return Number(response.data.data?.totalUnread || 0);
  },

  async getConversationMessages(
    conversationId: string,
    params?: { limit?: number; before?: string; starredOnly?: boolean }
  ): Promise<{ messages: SmsMessage[]; hasMore: boolean }> {
    const response = await apiClient.get<any>(`/sms/conversations/${conversationId}/messages`, {
      params,
    });
    return response.data.data || { messages: [], hasMore: false };
  },

  async assignConversation(conversationId: string, agentId: string): Promise<void> {
    await apiClient.patch(`/sms/conversations/${conversationId}/assign`, { agentId });
  },

  async assignConversationToMe(conversationId: string): Promise<void> {
    await apiClient.patch(`/sms/conversations/${conversationId}/assign-me`);
  },

  async setConversationStarred(conversationId: string, starred: boolean): Promise<{ is_starred: boolean }> {
    const response = await apiClient.patch<any>(`/sms/conversations/${conversationId}/star`, { starred });
    return response.data.data || { is_starred: starred };
  },

  async setMessageStarred(messageId: string, starred: boolean): Promise<{ is_starred: boolean }> {
    const response = await apiClient.patch<any>(`/sms/messages/${messageId}/star`, { starred });
    return response.data.data || { is_starred: starred };
  },

  async updateConversationStatus(conversationId: string, status: 'open' | 'assigned' | 'closed'): Promise<void> {
    await apiClient.patch(`/sms/conversations/${conversationId}/status`, { status });
  },

  async sendReply(conversationId: string, body: string): Promise<SmsMessage> {
    const response = await apiClient.post<any>('/sms/reply', { conversationId, body });
    return response.data.data;
  },

  async prepareConversationCall(conversationId: string): Promise<{
    callAttemptId: string;
    routingAttemptId: string;
    routingLegId: string | null;
    fromNumber: string;
    toNumber: string;
    leadId: string;
    leadName: string | null;
    leadPhone: string | null;
    leadEmail: string | null;
    companyName: string | null;
    campaignId: string;
    campaignName: string | null;
    scriptName: string | null;
    scriptContent: string | null;
    scriptStructure?: Record<string, any> | null;
    agentId: string;
    leadPhoneNumberId?: string;
    phoneNumberCount?: number;
    activePhoneNumberCount?: number;
    leadStatus?: string | null;
    subStageId?: string | null;
  }> {
    const response = await apiClient.post<any>(`/sms/conversations/${conversationId}/call`);
    return response.data.data;
  },

  async sendApp(conversationId: string, applicationUrl?: string): Promise<{
    message: SmsMessage;
    applicationUrl: string;
    leadStatus: string;
  }> {
    const response = await apiClient.post<any>(`/sms/conversations/${conversationId}/send-app`, {
      applicationUrl,
    });
    return response.data.data;
  },

  async getCampaignSmsStats(campaignId: string): Promise<SmsCampaignStats> {
    const response = await apiClient.get<any>(`/campaigns/${campaignId}/sms-stats`);
    return response.data.data;
  },

  async getCampaignSmsResults(
    campaignId: string,
    params: {
      page?: number;
      limit?: number;
      status?: string[];
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      sortBy?: 'statusChangedAt' | 'lastOutboundAt';
      sortOrder?: 'asc' | 'desc';
    } = {}
  ): Promise<{
    leads: SmsCampaignResultLead[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status?.length) query.set('status', params.status.join(','));
    if (params.search) query.set('search', params.search);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params.dateTo) query.set('dateTo', params.dateTo);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);
    const response = await apiClient.get<any>(`/campaigns/${campaignId}/sms-results?${query.toString()}`);
    return response.data.data;
  },

  async updateSequenceSteps(campaignId: string, steps: Array<{ stepNumber: number; templateBody: string; delayHours: number }>): Promise<void> {
    await apiClient.put(`/sms/campaigns/${campaignId}/sequence-steps`, { steps });
  },

  async getCampaignSequenceSteps(campaignId: string): Promise<any[]> {
    const response = await apiClient.get<any>(`/sms/campaigns/${campaignId}/sequence-steps`);
    return response.data.data || [];
  },

  async getGlobalTemplates(): Promise<{
    templates: SmsGlobalTemplate[];
    message: SmsGlobalTemplate[];
    sequence: SmsGlobalTemplate[];
  }> {
    const response = await apiClient.get('/sms/global-templates');
    return response.data.data;
  },

  async listAdminGlobalTemplates(): Promise<{
    templates: SmsGlobalTemplate[];
    message: SmsGlobalTemplate[];
    sequence: SmsGlobalTemplate[];
  }> {
    const response = await apiClient.get('/admin/sms-global-templates');
    return response.data.data;
  },

  async createGlobalTemplate(payload: {
    name: string;
    templateType: SmsGlobalTemplateType;
    description?: string | null;
    sortOrder?: number;
    steps: SmsGlobalTemplateStep[];
  }): Promise<SmsGlobalTemplate> {
    const response = await apiClient.post('/admin/sms-global-templates', payload);
    return response.data.data.template;
  },

  async updateGlobalTemplate(
    id: string,
    payload: {
      name?: string;
      templateType?: SmsGlobalTemplateType;
      description?: string | null;
      sortOrder?: number;
      isActive?: boolean;
      steps?: SmsGlobalTemplateStep[];
    }
  ): Promise<SmsGlobalTemplate> {
    const response = await apiClient.put(`/admin/sms-global-templates/${id}`, payload);
    return response.data.data.template;
  },

  async deactivateGlobalTemplate(id: string): Promise<void> {
    await apiClient.delete(`/admin/sms-global-templates/${id}`);
  },
};

export const merchantFileService = {
  async getMerchantFile(leadId: string): Promise<MerchantFileData> {
    const response = await apiClient.get<any>(`/leads/${leadId}/merchant-file`);
    return response.data.data;
  },

  async uploadDocument(
    leadId: string,
    categorySlug: string,
    file: File,
    bankAccountId?: string
  ): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    form.append('categorySlug', categorySlug);
    if (bankAccountId) form.append('bankAccountId', bankAccountId);
    await apiClient.post(`/leads/${leadId}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  async updateMilestones(
    leadId: string,
    patch: Partial<{
      linkOpenedAt: string | null;
      appDoneAt: string | null;
      docsUploadedAt: string | null;
      completedAt: string | null;
      applicationUrl: string | null;
    }>
  ) {
    const response = await apiClient.patch<any>(`/leads/${leadId}/application/milestones`, patch);
    return response.data.data;
  },

  async updateCategoryStatus(leadId: string, categoryId: string, status: string) {
    const response = await apiClient.patch<any>(
      `/leads/${leadId}/documents/categories/${categoryId}`,
      { status }
    );
    return response.data.data;
  },

  async updateBankAccountStatus(leadId: string, bankAccountId: string, status: string) {
    const response = await apiClient.patch<any>(
      `/leads/${leadId}/bank-accounts/${bankAccountId}`,
      { status }
    );
    return response.data.data;
  },
};
